import { STATUSPAGE } from '@repo/ui';
import {
  DeleteQuestionUseCaseInterface,
  CreateQuestionUseCaseInterface,
  GetQuestionByIdUseCaseInterface,
  UpdateQuestionUseCaseInterface,
  GetAllQuestionsUseCaseInterface,
  ReorderQuestionsUseCaseInterface,
  CloneQuestionUseCaseInterface,
} from 'domain/usecases';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  loadQuestions,
  clearQuestions,
  loadSelectedQuestion,
  clearSelectedQuestion,
} from 'presentation/config/store/evaluation-criteria/questions/questions-slice';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

export const useQuestions = () => {
  // States
  const [getAllStatus, setGetAllStatus] = useState<STATUSPAGE>(STATUSPAGE.DONE);
  const [createStatus, setCreateStatus] = useState<STATUSPAGE>(STATUSPAGE.DONE);
  const [updateStatus, setUpdateStatus] = useState<STATUSPAGE>(STATUSPAGE.DONE);
  const [getByIdStatus, setGetByIdStatus] = useState<STATUSPAGE>(
    STATUSPAGE.DONE,
  );
  const [cloneStatus, setCloneStatus] = useState<STATUSPAGE>(STATUSPAGE.DONE);

  const { questions, selectedQuestion } = useAppSelector(
    (state) => state.Questions,
  );

  // Hooks
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { questionsService } = useService();

  // Constants
  const areInputsAndBtnsDisabled =
    getByIdStatus === STATUSPAGE.LOADING || createStatus === STATUSPAGE.LOADING;

  // Methods
  const requestClearSelectedQuestion = useCallback(() => {
    dispatch(clearSelectedQuestion());
  }, []);

  const requestGetAllQuestions = useCallback(
    async (params: GetAllQuestionsUseCaseInterface.Params) => {
      setGetAllStatus(STATUSPAGE.LOADING);
      await questionsService
        .getAllQuestions(params)
        .then((res) => {
          dispatch(loadQuestions(res));
        })
        .catch((res) => {
          notifyError(res);
          dispatch(clearQuestions());
        })
        .finally(() => {
          setGetAllStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestReorderQuestions = useCallback(
    async (params: ReorderQuestionsUseCaseInterface.Params) => {
      await questionsService
        .reorderQuestions(params)
        .then((res) => {
          notifySuccess(res.message);
        })
        .catch((res) => {
          notifyError(res);
        });
    },
    [],
  );

  const requestCreateQuestion = useCallback(
    async (params: CreateQuestionUseCaseInterface.Params) => {
      setCreateStatus(STATUSPAGE.LOADING);
      await questionsService
        .createQuestion(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllQuestions({ criteriaId: id });
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setCreateStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetQuestionById = useCallback(
    async (params: GetQuestionByIdUseCaseInterface.Params) => {
      setGetByIdStatus(STATUSPAGE.LOADING);
      await questionsService
        .getQuestionById(params)
        .then((res) => {
          dispatch(loadSelectedQuestion(res));
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setGetByIdStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestUpdateQuestion = useCallback(
    async (
      params: UpdateQuestionUseCaseInterface.Params,
      toggleModalEdit: () => void,
    ) => {
      setUpdateStatus(STATUSPAGE.LOADING);
      await questionsService
        .updateQuestion(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllQuestions({ criteriaId: id });
          toggleModalEdit();
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setUpdateStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestDeleteQuestion = useCallback(
    async (params: DeleteQuestionUseCaseInterface.Params) => {
      await questionsService
        .deleteQuestion(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllQuestions({ criteriaId: id });
        })
        .catch((err) => {
          notifyError(err.message || 'Não foi possível excluir a questão');
        });
    },
    [],
  );

  const requestCopyQuestion = useCallback(
    async (
      params: CloneQuestionUseCaseInterface.Params,
      toggleModalCopyCriteria: () => void,
      handleEdit: (id: number) => void,
    ) => {
      setCloneStatus(STATUSPAGE.LOADING);
      await questionsService
        .cloneQuestion(params)
        .then((res) => {
          notifySuccess(res.message);
          toggleModalCopyCriteria();
          requestGetAllQuestions({ criteriaId: id });
          handleEdit(Number(res.questionId));
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setCloneStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  return {
    // states & data
    navigate,
    getAllStatus,
    getByIdStatus,
    cloneStatus,
    createStatus,
    updateStatus,
    areInputsAndBtnsDisabled,
    questions,
    selectedQuestion,

    // methods
    requestCopyQuestion,
    requestCreateQuestion,
    requestGetAllQuestions,
    requestDeleteQuestion,
    requestUpdateQuestion,
    requestGetQuestionById,
    requestReorderQuestions,
    requestClearSelectedQuestion,
  };
};
